import React from 'react';
import { TypeMaster } from '../../../../../services/apiTypes';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  MANAGE_ITEM_ROW_TYPE,
  MANAGE_ITEM_TEMP_ID,
} from '../../../../../_constants/manageConst';
import { CategoryMaster, ItemMaster } from '../../../../../services/apiTypes';
import useSortItem from '../../../hooks/useSortItem';
import TypeRow from './TypeRow';
import ItemDepth0Items from './Depth0Items/Depth0Items';
import { Options } from '../../../ManageInspectionItem';

type typeItemsProps = {
  // 中項目小項目共通props
  categoryMaster: CategoryMaster[];
  setCategoryMaster: React.Dispatch<React.SetStateAction<CategoryMaster[]>>;
  typeMaster: TypeMaster[];
  setTypeMaster: React.Dispatch<React.SetStateAction<TypeMaster[]>>;
  itemMaster: ItemMaster[];
  setItemMaster: React.Dispatch<React.SetStateAction<ItemMaster[]>>;
  displayMode: number;
  isAdmin: boolean;
  isOpenAccordions: Record<string, boolean>;
  handleAccordion: (id: string) => void;
  stickyPosition: number[];
  isSetPFRCodes: (item: ItemMaster) => boolean;
  isDisabledBtn: boolean;
  // 中項目props
  category: CategoryMaster;
  targetType: TypeMaster;
  onClickTypeAddIcon: (categoryId: string, typeId: string) => void;
  onClickTypeEditIcon: (data: TypeMaster) => void;
  onClickTypeDeleteIcon: (data: TypeMaster) => void;
  onClickTypeAddSave: (
    typeName: string,
    displayType: number,
    isOptionalAddition: boolean,
  ) => void;
  onClickTypeAddCancel: () => void;
  // 小項目props
  options: Options;
  targetItem: ItemMaster;
  onClickItemAddIcon: (data: ItemMaster) => void;
  onClickItemEditIcon: (data: ItemMaster) => void;
  handleItemInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    item: ItemMaster,
  ) => ItemMaster;
  checkInputForCondition: (item: ItemMaster) => boolean;
  onClickItemNamesEdit: (item: ItemMaster) => void;
  onClickItemEditSave: (item: ItemMaster) => void;
  onClickItemEditCancel: () => void;
  onClickItemDeleteIcon: (data: ItemMaster) => void;
};

const TypeItems: React.FC<typeItemsProps> = ({
  categoryMaster,
  setCategoryMaster,
  typeMaster,
  setTypeMaster,
  itemMaster,
  setItemMaster,
  displayMode,
  isAdmin,
  isOpenAccordions,
  handleAccordion,
  stickyPosition,
  isSetPFRCodes,
  isDisabledBtn,
  category,
  targetType,
  onClickTypeAddIcon,
  onClickTypeEditIcon,
  onClickTypeDeleteIcon,
  onClickTypeAddSave,
  onClickTypeAddCancel,
  options,
  targetItem,
  onClickItemAddIcon,
  onClickItemEditIcon,
  handleItemInputChange,
  checkInputForCondition,
  onClickItemNamesEdit,
  onClickItemEditSave,
  onClickItemEditCancel,
  onClickItemDeleteIcon,
}) => {
  const { SortableItem } = useSortItem({
    categoryMaster,
    setCategoryMaster,
    typeMaster,
    setTypeMaster,
    itemMaster,
    setItemMaster,
    displayMode,
    isSetPFRCodes,
    targetItem,
  });

  return (
    <SortableContext
      id={MANAGE_ITEM_ROW_TYPE.TYPE}
      items={typeMaster
        .filter((t) => t.categoryId === category.categoryId)
        .map((type) => type.typeId)}
      strategy={verticalListSortingStrategy}
    >
      {typeMaster
        .filter((t) => t.categoryId === category.categoryId)
        .map((type) => {
          const isAddType = type.typeId === MANAGE_ITEM_TEMP_ID.TYPE;
          // 中項目
          return (
            isOpenAccordions[category.categoryId] && (
              <React.Fragment key={`type_${type.typeId}`}>
                <SortableItem
                  key={`type_${type.typeId}`}
                  id={type.typeId}
                  name={MANAGE_ITEM_ROW_TYPE.TYPE}
                >
                  <TypeRow
                    type={isAddType ? targetType : type}
                    mode={displayMode}
                    isAdd={isAddType}
                    isAdmin={isAdmin}
                    stickyPosition={stickyPosition}
                    countItems={
                      itemMaster.filter((i) => {
                        if (i.typeId === type.typeId) {
                          if (i.depth === 0) {
                            return true;
                          } else if (i.parentItemId) {
                            if (i.depth === 1) {
                              return isOpenAccordions[i.parentItemId];
                            } else {
                              // 階層2は階層1を取得
                              const depth1 =
                                itemMaster.find(
                                  (i2) => i2.itemId === i.parentItemId,
                                ) ?? '';
                              if (depth1 && depth1.parentItemId) {
                                // この項目が親で開いている
                                return (
                                  isOpenAccordions[depth1.parentItemId] &&
                                  isOpenAccordions[depth1.itemId]
                                );
                              }
                              return false;
                            }
                          }
                        } else {
                          return false;
                        }
                      }).length
                    }
                    isDisabledBtn={isDisabledBtn}
                    isOpenAccordion={isOpenAccordions[type.typeId]}
                    onClickAccordion={handleAccordion}
                    onClickAddIcon={onClickTypeAddIcon}
                    onClickEditIcon={onClickTypeEditIcon}
                    onClickDeleteIcon={onClickTypeDeleteIcon}
                    onClickOk={onClickTypeAddSave}
                    onClickCancel={onClickTypeAddCancel}
                  />
                </SortableItem>
                <ItemDepth0Items
                  type={type}
                  targetItem={targetItem}
                  categoryMaster={categoryMaster}
                  setCategoryMaster={setCategoryMaster}
                  typeMaster={typeMaster}
                  setTypeMaster={setTypeMaster}
                  itemMaster={itemMaster}
                  setItemMaster={setItemMaster}
                  displayMode={displayMode}
                  isAdmin={isAdmin}
                  isOpenAccordions={isOpenAccordions}
                  handleAccordion={handleAccordion}
                  stickyPosition={stickyPosition}
                  isSetPFRCodes={isSetPFRCodes}
                  isDisabledBtn={isDisabledBtn}
                  options={options}
                  onClickItemAddIcon={onClickItemAddIcon}
                  onClickItemEditIcon={onClickItemEditIcon}
                  handleItemInputChange={handleItemInputChange}
                  checkInputForCondition={checkInputForCondition}
                  onClickItemNamesEdit={onClickItemNamesEdit}
                  onClickItemEditSave={onClickItemEditSave}
                  onClickItemEditCancel={onClickItemEditCancel}
                  onClickItemDeleteIcon={onClickItemDeleteIcon}
                />
              </React.Fragment>
            )
          );
        })}
    </SortableContext>
  );
};

export default TypeItems;
